import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';

import {
  BALANCE_SHEET_CATEGORIES,
  ChartOfAccountsDisplay,
  COADisplayAccount,
  P_AND_L_CATEGORIES
} from '../../../../chart-of-accounts/types';
import { getFlattenTreeData, getParsedAccounts } from '../../../../chart-of-accounts/utils';
import { COA_RECLASSIFY_FORM_FIELD_IDS } from './constants';
import { useIsBalanceSheet } from '../../../hooks/useIsBalanceSheet';

const mapToDisplayAccount = (account: COADisplayAccount): ChartOfAccountsDisplay =>
  ({
    id: account.id,
    name: account.name,
    code: account.code || '',
    category: account.category || ''
  } as ChartOfAccountsDisplay);

export const useReclassifyForm = (
  profitAndLossTree: COADisplayAccount | null,
  balanceSheetTree: COADisplayAccount | null,
  accountToReclassify?: COADisplayAccount
) => {
  const { accountCategoryNames } = useLocalisedConstantsContext();
  const intl = useIntl();

  const isBalanceSheet = useIsBalanceSheet();
  
  const categoriesForDisplay = isBalanceSheet ? BALANCE_SHEET_CATEGORIES : P_AND_L_CATEGORIES;

  const [selectedAccount, setSelectedAccount] = useState<ChartOfAccountsDisplay | null>(
    accountToReclassify ? mapToDisplayAccount(accountToReclassify) : null
  );

  const [categoryToMoveTo, setCategoryToMoveTo] = useState('');
  const [errors, setErrors] = useState<Record<string, { id: string; message: string }>>({});

  const hasBeenValidated = useRef(false);

  useEffect(() => {
    if (accountToReclassify) {
      setSelectedAccount(mapToDisplayAccount(accountToReclassify));
    } else {
      setSelectedAccount(null);
    }

    setCategoryToMoveTo('');
    setErrors({});
    hasBeenValidated.current = false;
  }, [accountToReclassify]);

  const accountsAutocompleteData = useMemo(() => {
    const activeTree = isBalanceSheet ? balanceSheetTree : profitAndLossTree;
    const transformedAccounts = getFlattenTreeData(activeTree?.children)
      .filter(a => a.type === 'account')
      .map(a => ({ code: a.code || '', name: a.name, category: a.category || '', id: a.id }));

    return getParsedAccounts(transformedAccounts as ChartOfAccountsDisplay[], isBalanceSheet, accountCategoryNames);
  }, [isBalanceSheet, balanceSheetTree, profitAndLossTree, accountCategoryNames]);

  const validate = useCallback(() => {
    const newErrors: Record<string, { id: string; message: string }> = {};

    if (!selectedAccount) {
      newErrors.name = {
        id: COA_RECLASSIFY_FORM_FIELD_IDS.SELECTED_ACCOUNT,
        message: intl.formatMessage({
          id: 'reporting.validation.account_is_required',
          defaultMessage: 'Account is required'
        })
      };
    }

    if (!categoryToMoveTo.trim().length) {
      newErrors.category = {
        id: COA_RECLASSIFY_FORM_FIELD_IDS.CATEGORY,
        message: intl.formatMessage({
          id: 'reporting.validation.category_is_required',
          defaultMessage: 'Category is required'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.keys(newErrors).length === 0;
  }, [selectedAccount, categoryToMoveTo, intl]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  return {
    selectedAccount,
    setSelectedAccount,
    categoryToMoveTo,
    setCategoryToMoveTo,
    accountOptions: accountsAutocompleteData,
    categoryOptions: categoriesForDisplay.filter(c => selectedAccount?.category !== c && c !== 'equity'),
    errors,
    validate,
    revalidate
  };
};
